import { useApi, PaginatedResponse } from '@/api';
import {
  Link,
  EtherscanLink,
  PageSkeleton,
  Page,
  Asset,
  TableCellBottom
} from '@/components';
import { POOL_ACTIVITY_EVENT } from '@/constants';

import { useParams } from 'react-router';
import {
  CurrencyValue,
  DataTable,
  Flex,
  RelativeDateTime,
  WalletAddress,
  TableSkeleton,
  useTableProps,
  DropdownFilter,
  formatDatetime
} from '@blockanalitica/ui';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import { formatEventName } from '@/utils/events';
import { useSearchParams } from '@/hooks';

type ActivityData = {
  wallet_address: string;
  tx_hash: string;
  order_index: string;
  datetime: string;
  block_number: number;
  event: string;
  amount: string | null;
  amount_usd: string | null;
  symbol: string;
  underlying_symbol: string;
  underlying_address: string;
  underlying_name: string;
};

export default function OverallEventsPage() {
  const { network } = useParams() as {
    network: string;
  };
  const { pagination, sorting, filters } = useTableProps({
    pageSize: 10,
    initialSorting: '-order_index',
    filtersDebounceTime: 500,
    initialFilters: {
      selectedOption: ''
    }
  });
  const [date] = useSearchParams('date', '');
  const [data, error] = useApi<PaginatedResponse<ActivityData[]>>({
    url: `/overall/events/`,
    queryParams: {
      page: pagination.currentPage,
      limit: pagination.pageSize,
      sort: sorting.currentSorting,
      event_type: filters.filtersApplied['selectedOption'],
      date: date
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return (
      <PageSkeleton>
        <TableSkeleton rows={10} columns={6} />
      </PageSkeleton>
    );
  }

  const columns: TableColumn[] = [
    {
      header: '',
      getCell: (row) => (
        <Link to={`/wallets/${row.wallet_address}`}>
          <WalletAddress address={row.wallet_address} />
        </Link>
      )
    },
    {
      header: 'Asset',
      getCell: (row) => (
        <Link to={`/markets/${row.underlying_address}`}>
          <Asset
            symbol={row.underlying_symbol}
            address={row.underlying_address}
            network={network}
            name={row.underlying_name}
            iconSize="3"
          />
        </Link>
      )
    },
    {
      header: 'Event',
      getCell: (row) => formatEventName(row.event)
    },
    {
      header: 'Amount',
      getCell: (row) => (
        <CurrencyValue
          value={row.amount}
          options={{
            currency: {
              name: row.underlying_symbol,
              address: row.underlying_address,
              network: network
            },
            trend: true,
            trendIcon: false
          }}
        />
      ),
      getCellBottom: (row) => (
        <TableCellBottom checkValue={row.amount_usd}>
          <CurrencyValue
            value={row.amount_usd}
            options={{
              currency: 'USD',
              absolute: true
            }}
          />
        </TableCellBottom>
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'amount'
    },
    {
      header: 'Time',
      getCell: (row) => (
        <Flex gap="xs" color="muted">
          <RelativeDateTime value={row.datetime} />
          <EtherscanLink value={row.tx_hash} network={network} />
        </Flex>
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'order_index'
    }
  ];
  return (
    <Page title={date ? `Events on ${formatDatetime(date)}` : 'Events'}>
      <Flex justifyContent="flex-end">
        <DropdownFilter
          options={POOL_ACTIVITY_EVENT}
          {...filters}
          withCryptoIcon={false}
        />
      </Flex>
      <DataTable
        rows={data.results}
        columns={columns}
        getKey={(row) => `${row.order_index}`}
        sorting={sorting}
        pagination={{
          ...pagination,
          totalRecords: data.pagination.total
        }}
      />
    </Page>
  );
}
